import React, { useEffect, useMemo } from 'react';
import { SlideProps } from './types';
import { useSlideTimeout } from './useSlideTimeout';
import { createComponentLogger } from './logger';
import Widget, { IWidget } from '../Widget';
import { DynamicObject } from '../../layout/View';

interface WidgetSlideProps extends SlideProps {
  widgets: DynamicObject<IWidget>;
}

// WidgetSlide: Displays a widget from the dashboard configuration and auto-completes after duration
const WidgetSlide: React.FC<WidgetSlideProps> = ({
  source,
  isActive,
  defaultDuration,
  onComplete,
  onError,
  widgets
}) => {
  // Create logger instance once
  const log = useMemo(() => createComponentLogger('WidgetSlide'), []);

  // Extract widget ID from source URL
  const widgetId = source.url;

  // Look up the widget from the widgets configuration
  const widget = useMemo(() => {
    const foundWidget = widgets[widgetId];
    if (!foundWidget) {
      log('⚠️  Widget not found', { widgetId, availableWidgets: Object.keys(widgets) });
    } else {
      log('✅ Widget found', { widgetId });
    }
    return foundWidget;
  }, [widgets, widgetId, log]);

  // Log component lifecycle
  useEffect(() => {
    log('🎬 WidgetSlide mounted', {
      isActive,
      widgetId,
      hasWidget: !!widget,
      defaultDuration,
      customDuration: source.duration
    });
  }, [log, isActive, widgetId, widget, source.duration, defaultDuration]);

  // Log active state changes
  useEffect(() => {
    if (isActive) {
      log('▶️  WidgetSlide activated', { widgetId });
    } else {
      log('⏸️  WidgetSlide deactivated');
    }
  }, [isActive, log, widgetId]);

  // If widget not found, trigger error
  useEffect(() => {
    if (!widget) {
      const error = new Error(`Widget with ID "${widgetId}" not found in dashboard configuration`);
      log('❌ Widget not found, triggering error', { widgetId });
      onError(error);
    }
  }, [widget, widgetId, onError, log]);

  // Use the shared timeout hook
  // Widgets are rendered immediately, so content is always ready
  useSlideTimeout({
    isActive,
    isContentReady: !!widget, // Widget is ready if it exists
    contentDuration: source.duration,
    defaultDuration,
    onComplete,
    componentName: 'WidgetSlide',
    log,
  });

  // If widget not found, don't render anything
  if (!widget) {
    return null;
  }

  // Render widget directly without wrapper to avoid layout issues with Reveal.js
  return <Widget widget={widget} id={widgetId} widgets={widgets} bareContent={true} />;
};

export default WidgetSlide;
